import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { visuallyHidden } from "@mui/utils";
import "./style.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as Urgent } from "../../../../assets/images/TicketImages/urgent.svg";
import { ReactComponent as High } from "../../../../assets/images/TicketImages/high.svg";
import { ReactComponent as Medium } from "../../../../assets/images/TicketImages/medium.svg";
import { ReactComponent as Low } from "../../../../assets/images/TicketImages/low.svg";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { updateTicket } from "../../../../store/features/updateTicket";
import { toast, ToastContainer } from "react-toastify";
import { styled } from "@mui/material/styles";
import { ReactComponent as Check } from "../../../../assets/images/check.svg";
import { chracterPosition, getStatusStyle } from "../../../../utils/index";
import TicketFilterToolBar from "./TicketFilterToolBar";
import { getAllTickets, buildTicketListParams } from "../../../../store/features/getAllTickets";

function normalizeTicketSortField(field) {
  if (!field) return "updatedAt";
  return field === "updated_at" ? "updatedAt" : field;
}

function descendingComparator(a, b, orderBy) {
  if (orderBy === "updatedAt") {
    const aRaw = a.updatedAt ?? a.updated_at;
    const bRaw = b.updatedAt ?? b.updated_at;
    const aTime = aRaw != null ? new Date(aRaw).getTime() : 0;
    const bTime = bRaw != null ? new Date(bRaw).getTime() : 0;
    if (bTime < aTime) return -1;
    if (bTime > aTime) return 1;
    return 0;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function TicketAllUser({
  setSelectedState,
  tickets,
  loading,
  searchQuery = "",
  initialSortOrder = "desc",
  initialSortField = "updatedAt",
  initialPage = 0,
  activeTab = 0,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pagination = useSelector((state) => state.tickets.pagination);
  const [searchParams, setSearchParams] = useSearchParams();
  const prevTabRef = useRef(searchParams.get("tab"));

  // Initialize states from URL params or props
  const [order, setOrder] = useState(
    () => initialSortOrder || searchParams.get("sortOrder") || "desc"
  );
  const [orderBy, setOrderBy] = useState(() =>
    normalizeTicketSortField(
      initialSortField || searchParams.get("sortField") || "updatedAt"
    )
  );
  const [page, setPage] = useState(
    () => initialPage || parseInt(searchParams.get("page") || "0", 10)
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [localTickets, setLocalTickets] = useState(tickets || []);

  // Watch for tab changes and reset local page only when tab actually changes
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    // Only reset page if tab has actually changed (not when returning from ticket details)
    if (currentTab !== undefined && currentTab !== prevTabRef.current) {
      prevTabRef.current = currentTab;
      setPage(0);
    }
  }, [searchParams.get("tab")]);

  
  useEffect(() => {
    const urlOrder = searchParams.get("sortOrder") || "desc";
    const urlField = normalizeTicketSortField(
      searchParams.get("sortField") || "updatedAt"
    );
    if (urlOrder === order && urlField === orderBy) return;
    const params = new URLSearchParams(searchParams);
    params.set("sortOrder", order);
    params.set("sortField", orderBy);
    setSearchParams(params);
  }, [order, orderBy, searchParams, setSearchParams]);

  useEffect(() => {
    if (initialSortOrder && initialSortOrder !== order) {
      setOrder(initialSortOrder);
    }
    const nextOrderBy = normalizeTicketSortField(initialSortField);
    if (initialSortField && nextOrderBy !== orderBy) {
      setOrderBy(nextOrderBy);
    }
  }, [initialSortOrder, initialSortField]);

  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );

  const t = useSelector(
    (state) => state.language.translations[currentLanguage]
  );

  const colorCodesForAvatar = [
    "#405261", "#FF4D4F", "#FA541C", "#FA8C16", "#FAAD14", "#EB2F96",
    "#FADB14", "#A0D911", "#52C41A", "#5CDBD3", "#9254DE", "#F759AB",
    "#2F54EB", "#722ED1", "#1890FF", "#13C2C2", "#2DB7F5", "#19BE6B",
    "#30A745", "#FA8C16", "#FAAD14", "#9254DE", "#F759AB", "#13C2C2",
    "#722ED1", "#2DB7F5",
  ];

  useEffect(() => {
    setLocalTickets(tickets || []);
  }, [tickets]);

  // Search is applied server-side, so the API page is already the result set.
  const filteredTickets = localTickets;

  const paginationCount = pagination?.total ?? 0;

  const visibleRows = useMemo(
    () => stableSort(filteredTickets, getComparator(order, orderBy)),
    [order, orderBy, filteredTickets]
  );

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    params.set("sortOrder", newOrder);
    params.set("sortField", normalizeTicketSortField(property));
    params.set("page", "0");
    setSearchParams(params);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Select only the tickets visible on the current page
      const newSelected = visibleRows.map((ticket) => ticket._id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };
  const handleClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleRowClick = (id) => {
    const params = new URLSearchParams(searchParams);
    params.set("ticketId", id);
    // Preserve current page and sort params
    params.set("page", page.toString());
    params.set("sortOrder", order);
    params.set("sortField", orderBy);
    navigate(`/ticketsection?${params.toString()}`);
    setSelectedState(2);
    setActiveRowId(id);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // setSelected([]);
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
    const params = new URLSearchParams(searchParams);
    params.set("page", "0");
    setSearchParams(params);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear() % 100
      }`;
  };

  const BlueRoundedCheckbox = styled(Checkbox)(({ theme }) => ({
    padding: "0px",
    width: "16px",
    height: "16px",
    marginLeft: "13px",
    marginRight: '14px',
    display: "flex",
    alignItems: "center",
    justifyContent: "center",


    "&.MuiCheckbox-root": {
      color: "transparent",
    },
    "& .MuiSvgIcon-root": {
      width: "16px",
      height: "16px",
      borderRadius: "4px",
      border: "1px solid #D5DCE1",
      backgroundColor: "#FFF",
      boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
      path: {
        display: "none",
      },
    },
    "&.Mui-checked": {
      "& .MuiSvgIcon-root": {
        border: "1px solid #148DFD",
        backgroundColor: "#FFF",
      },
    },
    "&:hover": {
      backgroundColor: "transparent",
      "& .MuiSvgIcon-root": {
        border: "1px solid #148DFD",
      },
    },
  }));
  const handleActionClick = (event, ticket) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedTicket(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setLocalTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket._id === selectedTicket._id
            ? { ...ticket, status: newStatus }
            : ticket
        )
      );

      await dispatch(
        updateTicket({
          ticketId: selectedTicket._id,
          updateData: { status: newStatus },
        })
      ).unwrap();

      toast.success("Status updated successfully");
      dispatch(getAllTickets(buildTicketListParams(activeTab, searchParams, rowsPerPage)));

      handleActionClose();
    } catch (error) {
      setLocalTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket._id === selectedTicket._id
            ? { ...ticket, status: selectedTicket.status }
            : ticket
        )
      );
      console.error("Failed to update status:", error);
      toast.error("Failed to update status");
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      setLocalTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket._id === selectedTicket._id
            ? { ...ticket, priority: newPriority }
            : ticket
        )
      );

      await dispatch(
        updateTicket({
          ticketId: selectedTicket._id,
          updateData: { priority: newPriority },
        })
      ).unwrap();

      toast.success("Priority updated successfully");
      handleActionClose();
    } catch (error) {
      setLocalTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket._id === selectedTicket._id
            ? { ...ticket, priority: selectedTicket.priority }
            : ticket
        )
      );
      console.error("Failed to update priority:", error);
      toast.error("Failed to update priority");
    }
  };

  const headCells = [
    { id: "_id", label: "ID", numeric: false },
    { id: "subject", label: t.SUBJECT, numeric: false },
    { id: "status", label: t.STATUSTB, numeric: false },

    { id: "assigneeName", label: t.ASSIGNEE, numeric: false },
    { id: "customerName", label: t.REQUESTER, numeric: false },

    { id: "updatedAt", label: "Waiting", numeric: false },
    { id: "actions", label: "", numeric: false },
  ];

  if (!tickets) {
    return null;
  }
  const toolbarRef = useRef(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  useEffect(() => {
    if (toolbarRef.current) {
      setToolbarHeight(toolbarRef.current.offsetHeight);
    }
  }, [selected.length]); // Recalculate when selection changes

  // Calculate dynamic height for table container
  const tableContainerHeight = useMemo(() => {
    const baseHeight = `calc(100vh - ${347
      + toolbarHeight}px)`;
    return baseHeight;
  }, [toolbarHeight]);

  return (
    <div className="Ticket-Section-Container  ">

      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, }}>

          <div ref={toolbarRef}>
            <TicketFilterToolBar
              numSelected={selected.length}
              t={t}
              selectedTickets={selected}
              setSelected={setSelected}
              setLocalTickets={setLocalTickets}
              tickets={tickets}
              activeTab={activeTab}
            />
          </div>
          {filteredTickets.length > 0 ? (
            <>
              <TableContainer sx={{
                boxShadow: "none",
                flex: 1,
                marginTop: '2px',
                marginBottom: '40px',
                height: tableContainerHeight,
                overflowY: "auto",

              }}>
                <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                  <TableHead>
                    <TableRow sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      background: "#fff",
                    }}>
                      <TableCell
                        padding="checkbox"
                      // sx={{ paddingBottom: "5px" }}
                      >
                        <BlueRoundedCheckbox
                          color="primary"
                          indeterminate={
                            selected.filter((id) =>
                              visibleRows.some((ticket) => ticket._id === id)
                            ).length > 0 &&
                            selected.filter((id) =>
                              visibleRows.some((ticket) => ticket._id === id)
                            ).length < visibleRows.length
                          }
                          checked={
                            visibleRows.length > 0 &&
                            visibleRows.every((ticket) => selected.includes(ticket._id))
                          }
                          checkedIcon={
                            <div style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '4px',
                              border: '1px solid #148DFD',
                              backgroundColor: '#FFF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Check style={{

                                color: '#148DFD'
                              }} />
                            </div>
                          }
                          onChange={handleSelectAllClick}
                          inputProps={{
                            "aria-label": "select all tickets",
                          }}
                          sx={{ marginBottom: "-15px" }}
                        />
                      </TableCell>
                      {headCells.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={headCell.numeric ? "right" : "left"}
                          sortDirection={
                            orderBy === headCell.id ? order : false
                          }
                          sx={{
                            paddingBottom: "0px",
                            fontWeight: "400",


                            fontSize: "12px",
                            color: "#405261",
                            ...(headCell.id === "_id" && {
                              // paddingRight: "14px",
                              // padding: '16px 0px',
                              paddingLeft: '0px'


                            }),
                          }}
                        >
                          {headCell.id === "updatedAt" ? (
                            <TableSortLabel
                              active={orderBy === headCell.id}
                              direction={
                                orderBy === headCell.id ? order : "asc"
                              }
                              onClick={(event) =>
                                handleRequestSort(event, headCell.id)
                              }
                            >
                              {headCell.label}
                              {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {order === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                                </Box>
                              ) : null}
                            </TableSortLabel>
                          ) : (
                            <span>{headCell.label}</span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map((ticket) => {
                      const isItemSelected = isSelected(ticket._id);
                      return (
                        <TableRow
                          hover
                          onClick={() => handleRowClick(ticket._id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={ticket._id}
                          selected={isItemSelected}
                          sx={{
                            "& td": { height: "4rem", py: 0, verticalAlign: "middle", },
                            ...(isItemSelected && {
                              backgroundColor: "#F7F8F9 !important", // light red for selected row

                            }),
                          }}
                        >
                          <TableCell padding="checkbox">
                            <BlueRoundedCheckbox
                              color="primary"
                              checked={isItemSelected}
                              onClick={(event) =>
                                handleClick(event, ticket._id)
                              }
                              checkedIcon={
                                <div style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '4px',
                                  border: '1px solid #148DFD',
                                  backgroundColor: '#FFF',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Check style={{

                                    color: '#148DFD'
                                  }} />
                                </div>
                              }
                            />
                          </TableCell>
                          <TableCell sx={{ padding: '0px', }}>#{ticket?._id?.slice(-6)}</TableCell>
                          <TableCell>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <Box
                                sx={{
                                  marginRight: "8px",
                                  display: "flex",
                                }}
                              >
                                {ticket.priority === "urgent" ? (
                                  <Urgent />
                                ) : ticket.priority === "high" ? (
                                  <High />
                                ) : ticket.priority === "medium" ? (
                                  <Medium />
                                ) : ticket.priority === "low" ? (
                                  <Box
                                    sx={{
                                      width: "16px",
                                      height: "16px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Low />
                                  </Box>
                                ) : null}
                              </Box>

                              {(() => {
                                const subject = ticket.subject || "N/A";
                                const capitalized =
                                  subject.charAt(0).toUpperCase() +
                                  subject.slice(1);

                                return capitalized.length > 32
                                  ? capitalized.slice(0, 32) + "..."
                                  : capitalized;
                              })()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span style={getStatusStyle(ticket.status)}>
                              {ticket.status?.charAt(0).toUpperCase() +
                                ticket.status?.slice(1)}
                            </span>
                          </TableCell>

                          <TableCell>
                            {ticket.assignee ? (
                              <div className="team-table-img">
                                {/* {ticket.assignee?.agent_image ? (
                                  <Avatar
                                    src={ticket.assignee?.agent_image}
                                    sx={{ width: "32px", height: "32px" }}
                                  />
                                ) : (
                                  <Avatar
                                    sx={{
                                      width: 32,
                                      height: 32,

                                      bgcolor:
                                        colorCodesForAvatar[
                                          chracterPosition(
                                            ticket.assignee.full_name.substring(0,1)
                                          )
                                        ],
                                    }}
                                  >
                                    {ticket.assignee.full_name?.substring(0, 1)?.toUpperCase()}
                                  </Avatar>
                                )} */}
                                <p className="team-table-rowtext">
                                  {ticket.assignee.full_name}
                                </p>
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <div className="team-table-img">
                              <Avatar
                                sx={{
                                  bgcolor:
                                    colorCodesForAvatar[
                                    chracterPosition(
                                      ticket.customerName
                                        ?.substring(0, 1)
                                        ?.toUpperCase()
                                    )
                                    ],
                                  width: "32px",
                                  height: "32px",
                                }}
                              >
                                {ticket.customerName
                                  ?.substring(0, 1)
                                  ?.toUpperCase()}
                              </Avatar>
                              <div>
                                <p className="team-table-rowtext truncate">
                                  {ticket.customerName.charAt(0).toUpperCase() +
                                    ticket.customerName.slice(1)}
                                </p>
                                <p
                                  className="truncate"
                                  style={{ fontSize: "14px", color: "#556575" }}
                                >
                                  {ticket.customerEmail &&
                                    typeof ticket.customerEmail === "string"
                                    ? ticket.customerEmail.toLowerCase()
                                    : ticket.customerEmail}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatTimestamp(ticket.updatedAt)}
                          </TableCell>
                          <TableCell padding="none">
                            <IconButton
                              onClick={(e) => handleActionClick(e, ticket)}
                              size="small"
                              sx={{
                                ml: 1,
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              }}
                            >
                              <MoreHorizIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow >
                      );
                    })}
                  </TableBody >
                </Table >
              </TableContainer >

            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Typography variant="h6" sx={{ color: "#8C8C8C" }}>
                {"No tickets available"}
              </Typography>
            </Box>
          )}
        </Paper >
      </Box >
      {
        filteredTickets.length !== 0 &&
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            width: "100%",
            background: "#fff",
            zIndex: 10,
            padding: "16px 24px",
            borderTop: "1px solid #E5E9EC",
          }}
        >
          <TablePagination
            component="div"
            count={paginationCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => (
              <Typography
                sx={{
                  color: "#405261",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                Tickets {from}-{to}
              </Typography>
            )}
            ActionsComponent={(props) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginLeft: "auto",
                  // marginTop: "15px",
                }}
              >
                <Button
                  onClick={(e) => props.onPageChange(e, props.page - 1)}
                  disabled={props.page === 0}
                  sx={{
                    width: "128px",
                    height: "40px",
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",
                    background: "#FFF",
                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",

                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {
                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Previous
                </Button>
                <Button
                  onClick={(e) => props.onPageChange(e, props.page + 1)}
                  disabled={
                    props.page >= Math.ceil(props.count / rowsPerPage) - 1
                  }
                  sx={{
                    width: "88px",
                    height: "40px",
                    flexShrink: 0,
                    borderRadius: "8px",
                    border: "1px solid #D5DCE1",

                    boxShadow: "0px 1px 2px 0px rgba(6, 21, 34, 0.05)",
                    color: "#405261",
                    backgroundColor: "#FFFFFF",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    textTransform: "none",
                    "&:not(.Mui-disabled):hover": {
                      backgroundColor: "#FFFFFF",

                      borderColor: "#C5CED6",
                    },
                    "&.Mui-disabled": {

                      color: "#93A1B0",
                      border: "1px solid #C5CED6",
                      bgcolor: "#F9FAFA",
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            sx={{
              ".MuiTablePagination-toolbar": {
                padding: 0,
                minHeight: "unset",
                display: "flex",
                justifyContent: "space-between",
              },
              ".MuiTablePagination-spacer": {
                display: "none",
              },
              ".MuiTablePagination-selectLabel": {
                display: "none",
              },
              ".MuiTablePagination-select": {
                display: "none",
              },
              ".MuiTablePagination-selectIcon": {
                display: "none",
              },
            }}
          /> </Box>
      }
      < Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            width: 200,
            padding: '8px',
            "& .MuiMenuItem-root": {
              px: '8px',
              py: 1,
              "&:hover": {


                color: '#061522',
                borderRadius: "8px",
                background: "#F9FAFA  !important",
              },
              "&.Mui-selected": {
                color: '#061522',
                borderRadius: "8px",
                background: "#F9FAFA !important",
              },
              "&.Mui-selected:hover": {
                background: "#F9FAFA  !important", // keep same on hover while selected
              },
            },
          },

        }}
      >
        {/* <MenuItem disabled sx={{ color: "#8C8C8C !important" }}>
          Mark as Priority
        </MenuItem> */}
        {
          ["urgent", "high", "medium", "low"].map((priority) => (
            <MenuItem
              key={priority}
              onClick={() => handlePriorityChange(priority)}
              selected={selectedTicket?.priority === priority}
            >
              <ListItemIcon>
                {priority === "urgent" && <Urgent />}
                {priority === "high" && <High />}
                {priority === "medium" && <Medium />}
                {priority === "low" && <Low style={{ marginLeft: "5px" }} />}
              </ListItemIcon>
              <ListItemText>
                {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
              </ListItemText>
            </MenuItem>
          ))
        }
        <Divider />
        {
          ["open", "pending", "solved", "spam"].map((status) => (
            <MenuItem
              key={status}
              onClick={() => handleStatusChange(status)}
              selected={selectedTicket?.status === status}
            >
              Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
            </MenuItem>
          ))
        }
      </Menu >
    </div >
  );
}

export default TicketAllUser;
